import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useBrand from '../../styles/theme/brands/useBrand';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  CircularProgress,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { authService } from '../../services/api';
import LanguageSelector from '../../components/LanguageSelector';

const PasswordReset = () => {
  // Login e redefinição de senha mostram sempre a marca Beworks.
  const { logo: beworksLogo } = useBrand('beworks');
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const navigate = useNavigate();

  const resetMutation = useMutation({
    mutationFn: (emailValue) => authService.passwordReset(emailValue),
    onSuccess: () => {
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    },
    onError: () => {
      setError(t('passwordReset.error'));
    },
  });

  const validateEmail = (value) => {
    return value.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError(t('passwordReset.fillEmail'));
      return;
    }

    if (!validateEmail(email)) {
      setError(t('passwordReset.invalidEmail'));
      return;
    }

    resetMutation.mutate(email);
  };

  const isPending = resetMutation.isPending;
  const isSuccess = resetMutation.isSuccess;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.paper',
        position: 'relative',
        px: 2,
      }}
    >
      {/* Language Selector - Top Right */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 16, sm: 20 },
          right: { xs: 16, sm: 20 },
        }}
      >
        <LanguageSelector />
      </Box>

      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'background.default',
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              width: 220,
              height: 43,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <img
              src={beworksLogo}
              alt="Beworks"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>

          <Typography
            component="h1"
            variant="h5"
            sx={{
              mb: 1,
              color: 'text.primary',
              textAlign: 'center',
            }}
          >
            {t('passwordReset.title')}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mb: 3,
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            {t('passwordReset.subtitle')}
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ width: '100%' }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {isSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('passwordReset.success')}
              </Alert>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('passwordReset.emailLabel')}
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isPending || isSuccess}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isPending || isSuccess}
              sx={{
                py: 1.5,
                mb: 2,
              }}
            >
              {isPending ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('passwordReset.sendButton')
              )}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Link
                to="/login"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <ArrowBack fontSize="small" />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {t('passwordReset.backToLogin')}
                </Typography>
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PasswordReset;
